import {
  BookOpen,
  CloudUpload,
  MessageSquareText,
  Minus,
  Plus,
  Rows3,
  Share2,
} from "lucide-react";

type Props = {
  kind: "pdf" | "docx";
  zoom: number;
  docxViewMode?: "continuous" | "paginated";
  /** Docx: whether inline/side comments are shown */
  showComments?: boolean;
  cloudSaving?: boolean;
  sharing?: boolean;
  signing?: boolean;
  canShare?: boolean;
  onZoomIn: () => void;
  onZoomOut: () => void;
  /** Tap zoom % → reset to 100% */
  onResetZoom: () => void;
  onDocxViewMode?: (mode: "continuous" | "paginated") => void;
  onToggleComments?: () => void;
  onShare?: () => void;
  onCloudSave?: () => void;
  onSign?: () => void;
};

/** Zoom / cloud controls under the chrome header — icons only. */
export function ReaderToolbar({
  kind,
  zoom,
  docxViewMode,
  showComments = true,
  cloudSaving,
  sharing,
  signing,
  canShare = true,
  onZoomIn,
  onZoomOut,
  onResetZoom,
  onDocxViewMode,
  onToggleComments,
  onShare,
  onCloudSave,
  onSign,
}: Props) {
  const zoomLabel = toPersianDigits(`${Math.round(zoom * 100)}٪`);

  return (
    <div className="reader-toolbar" role="toolbar" aria-label="ابزار نمایش">
      <div className="reader-toolbar-scroll">
        {kind === "docx" && onDocxViewMode && (
          <>
            <button
              type="button"
              className={`icon-btn compact${docxViewMode === "continuous" ? " active" : ""}`}
              title="پیوسته"
              aria-label="پیوسته"
              aria-pressed={docxViewMode === "continuous"}
              onClick={() => onDocxViewMode("continuous")}
            >
              <Rows3 size={15} aria-hidden />
            </button>
            <button
              type="button"
              className={`icon-btn compact${docxViewMode === "paginated" ? " active" : ""}`}
              title="صفحه‌بندی"
              aria-label="صفحه‌بندی"
              aria-pressed={docxViewMode === "paginated"}
              onClick={() => onDocxViewMode("paginated")}
            >
              <BookOpen size={15} aria-hidden />
            </button>
            <span className="ft-sep" aria-hidden />
          </>
        )}
        {(kind === "docx" || kind === "pdf") && onToggleComments && (
          <>
            <button
              type="button"
              className={`icon-btn compact${showComments ? " active" : ""}`}
              title={showComments ? "مخفی کردن نظرات" : "نمایش نظرات"}
              aria-pressed={showComments}
              aria-label={showComments ? "مخفی کردن نظرات" : "نمایش نظرات"}
              onClick={onToggleComments}
            >
              <MessageSquareText size={15} aria-hidden />
            </button>
            <span className="ft-sep" aria-hidden />
          </>
        )}
        <button
          type="button"
          className="icon-btn compact"
          aria-label="کوچک‌نمایی"
          title="کوچک‌نمایی"
          onClick={onZoomOut}
        >
          <Minus size={15} aria-hidden />
        </button>
        <button
          type="button"
          className="page-indicator zoom-label zoom-reset"
          dir="ltr"
          title="بازنشانی به ۱۰۰٪"
          aria-label="بازنشانی زوم به ۱۰۰٪"
          onClick={onResetZoom}
        >
          {zoomLabel}
        </button>
        <button
          type="button"
          className="icon-btn compact"
          aria-label="بزرگ‌نمایی"
          title="بزرگ‌نمایی"
          onClick={onZoomIn}
        >
          <Plus size={15} aria-hidden />
        </button>
        {(onShare || onCloudSave || onSign) && (
          <span className="ft-sep" aria-hidden />
        )}
        {onSign && (
          <button
            type="button"
            className="icon-btn compact"
            disabled={signing}
            onClick={onSign}
            title="امضای سند"
            aria-label="امضای سند"
          >
            <img
              src="/brand/nevisign-logo.svg"
              className="nevisign-toolbar-logo"
              alt=""
              aria-hidden="true"
            />
          </button>
        )}
        {onShare && (
          <button
            type="button"
            className="icon-btn compact"
            disabled={!canShare || sharing}
            onClick={onShare}
            title="اشتراک فایل"
            aria-label="اشتراک فایل"
          >
            <Share2 size={15} aria-hidden />
          </button>
        )}
        {onCloudSave && (
          <button
            type="button"
            className="icon-btn compact"
            disabled={cloudSaving}
            onClick={onCloudSave}
            title="ذخیره در نویساین"
            aria-label="ذخیره در نویساین"
          >
            <CloudUpload size={15} aria-hidden />
          </button>
        )}
      </div>
    </div>
  );
}

/** Convert Western digits to Persian for page / zoom labels. */
export function toPersianDigits(input: string): string {
  return input.replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[Number(d)] ?? d);
}
